export type NavItem = { label: string; href: string };

export const navItems: NavItem[] = [
  { label: "Home", href: "/" },
  { label: "How to use", href: "/tutorial" },
  { label: "Features", href: "/features" },
  { label: "Install", href: "/install" },
  { label: "Docs", href: "/docs" },
  { label: "Blog", href: "/blog" },
  { label: "About", href: "/about" },
  { label: "Contact", href: "/contact" },
];

export type BlogPost = { slug: string; title: string; description: string; date: string };

export const blogPosts: BlogPost[] = [
  {
    slug: "drag-and-drop-online-orders-ecommerce-write-api",
    title: "A New Idea in E-commerce: Drag an Order Across the Counter and Your Online Store Updates Itself",
    description:
      "Online orders land on a drag-and-drop board in your POS. Move a card to Packing or Ready and the storefront hears about it — through a signed webhook, a write API, or a push to WooCommerce and Shopify.",
    date: "2026-10-07",
  },
  {
    slug: "connect-your-online-store-to-nodedr-pos",
    title: "Connect Your Online Store to NodeDR POS: One Stock Count, Not Two",
    description:
      "How the External Stock API lets your e-commerce storefront read and update the same inventory your counter uses, with a webhook to push changes live.",
    date: "2026-08-30",
  },
  {
    slug: "why-a-small-shop-needs-a-real-pos",
    title: "Why a Small Shop Needs a Real POS (Not Just a Notebook or a Spreadsheet)",
    description:
      "Barcode scanning, stock counts that stay accurate, and a dashboard that shows what's actually selling — what changes when a counter moves off paper.",
    date: "2026-07-28",
  },
  {
    slug: "nodedr-pos-is-actually-free",
    title: "NodeDR POS Is Actually Free — Here's What That Means in Practice",
    description:
      "AGPL-3.0-licensed, no subscription, no per-transaction fee, no premium tier. What \"free and open source\" actually guarantees, and where the real cost of running it goes instead.",
    date: "2026-07-28",
  },
  {
    slug: "barcode-checkout-and-gst-billing",
    title: "How NodeDR POS Handles Barcode Checkout and GST Billing Correctly",
    description:
      "Scanner-speed checkout without a barcode database subscription, and GST-inclusive MRP pricing that breaks CGST/SGST out correctly on every receipt.",
    date: "2026-07-28",
  },
  {
    slug: "why-self-hosting-your-pos-matters",
    title: "Why Self-Hosting Your POS Matters More Than It Sounds Like It Should",
    description:
      "Sales data is the most sensitive record a small business keeps. What changes when it lives on your own machine instead of a vendor's cloud.",
    date: "2026-07-28",
  },
];

export const legalNavItems: NavItem[] = [
  { label: "Privacy", href: "/privacy" },
  { label: "Terms", href: "/terms" },
  { label: "Accessibility", href: "/accessibility" },
];

export type Feature = {
  title: string;
  description: string;
  icon: string;
};

export const features: Feature[] = [
  {
    title: "Barcode-driven checkout",
    description:
      "Scan to add items, scan again to bump quantity, press Enter to finalize. Unknown barcodes surface a clear toast — no register blocking. Works with any USB HID barcode scanner out of the box.",
    icon: "scan-barcode",
  },
  {
    title: "Camera barcode & QR scanning",
    description:
      "No USB scanner needed — scan with a phone or tablet camera at checkout or when adding a product. Requests a 1080p frame with continuous autofocus and feeds the exact same lookup/add-to-cart logic as a hardware scanner.",
    icon: "camera",
  },
  {
    title: "GST billing & compliance",
    description:
      "Per-product GST rates with HSN/SAC codes. MRP is treated as GST-inclusive as required by Indian law — CGST/SGST are broken out on every receipt. Live GSTIN and PAN format validation built in.",
    icon: "receipt-indian-rupee",
  },
  {
    title: "Cash, UPI & card payments",
    description:
      "Multiple payment methods at checkout — cash (with change calculation), UPI, and card. Mixed payment is supported. Payment method mix is visible on the sales dashboard.",
    icon: "credit-card",
  },
  {
    title: "Thermal receipt printing",
    description:
      "Three print paths: browser print dialog (any CUPS/OS printer), server-side PDF download, or direct ESC/POS over USB — receipt cuts off the roll without a dialog. Auto-print after every sale is one setting toggle.",
    icon: "printer",
  },
  {
    title: "Loyalty & customer dues",
    description:
      "Customers earn points on every purchase, redeemable at checkout. Udhaar (credit) sales track dues against a customer with full payment history. Top customers ranked by point balance on the dashboard.",
    icon: "star",
  },
  {
    title: "Offline barcode generator",
    description:
      "No barcode on a product? Generate a valid EAN-13 using the GS1 internal-use prefix range, or print a QR label — all client-side, no network call. Works fully offline.",
    icon: "qr-code",
  },
  {
    title: "Returns & exchanges",
    description:
      "Look up any past bill by invoice number from the register, queue lines to return, and settle as a refund or fold straight into a new exchange sale. Returned stock auto-restocks. A unit can never be returned twice.",
    icon: "arrow-left-right",
  },
  {
    title: "Inventory management",
    description:
      "Scan to edit stock, auto-fill the new product form from online product databases on an unknown barcode, product photos, low-stock dashboard alerts, per-product discount %, and an optional 'allow negative stock' mode to keep the register open when counts lag reality.",
    icon: "package",
  },
  {
    title: "Sales dashboard & CSV export",
    description:
      "Revenue trend, best sellers, payment method mix, top loyalty customers, and today's totals. One-click CSV export of full sales history. Searchable invoice history with reprint from any past sale.",
    icon: "bar-chart-3",
  },
  {
    title: "Staff accounts & roles",
    description:
      "Admin and cashier roles. Cashiers run the register; admins manage staff, settings, inventory, and exports. Bcrypt-hashed passwords and HttpOnly JWT sessions.",
    icon: "users",
  },
  {
    title: "Works on LAN tablets & phones",
    description:
      "Access at http://machine:1994 from any device on the shop network — the nav collapses to a slide-out drawer on mobile. No per-device install needed.",
    icon: "tablet-smartphone",
  },
  {
    title: "One-click CasaOS / ZimaOS install",
    description:
      "Already run a CasaOS or ZimaOS home server? Install straight from a compose URL — no terminal, no git clone. Pre-built images for amd64 and arm64, data persists in the standard CasaOS AppData location.",
    icon: "server",
  },
  {
    title: "Live e-commerce stock sync",
    description:
      "Connect your online store with a key-authenticated API, no extra service to run. Link a product by SKU and stock stays in sync both ways — a sale on your website decrements the shop's real count, and a sale at the counter reflects on the site within moments via a signed webhook.",
    icon: "link",
  },
  {
    title: "Dark & light theme",
    description:
      "Toggle between a premium dark theme and a light theme from the header. The choice is remembered per device and defaults to your system preference.",
    icon: "sun-moon",
  },
  {
    title: "Private by design",
    description:
      "No analytics, no telemetry, no cloud dependency. The only outbound requests are optional ones you trigger: update checks and the \"Find online\" product lookup (only the barcode or search text is sent). Your sales data never leaves the shop.",
    icon: "shield-check",
  },
];

export type Screenshot = {
  src: string;
  alt: string;
  caption: string;
};

export const screenshots: Screenshot[] = [
  {
    src: "/screenshots/dashboard.png",
    alt: "NodeDR POS dashboard with today's revenue, a 14-day revenue chart, low inventory and online orders waiting",
    caption: "Dashboard — today's revenue, 14-day trend, low stock, online orders waiting, customer dues",
  },
  {
    src: "/screenshots/orders.png",
    alt: "NodeDR POS Online orders board with New, Packing, Ready and Collected columns you drag cards across",
    caption: "Online orders — drag a card New → Packing → Ready and your store hears about it",
  },
  {
    src: "/screenshots/pos.png",
    alt: "NodeDR POS checkout with a cart, a weighed item and a dynamic UPI QR code",
    caption: "POS checkout — scan, weigh, and take UPI with a QR for the exact amount",
  },
  {
    src: "/screenshots/reports.png",
    alt: "NodeDR POS reports with revenue, margin, category split and an hourly heatmap",
    caption: "Reports — revenue, margin, category split, hourly heatmap, week-on-week",
  },
  {
    src: "/screenshots/inventory.png",
    alt: "NodeDR POS inventory list with bulk import and label actions",
    caption: "Inventory — bulk import, low-stock highlights, per-product GST and labels",
  },
  {
    src: "/screenshots/add-product.png",
    alt: "NodeDR POS add product dialog with Generate barcode and Scan with camera buttons",
    caption: "Add product — generate a barcode offline or scan one with the camera",
  },
  {
    src: "/screenshots/invoice-a4.png",
    alt: "NodeDR POS A4 tax invoice with warranty table and authorised signature",
    caption: "A4 tax invoice — GST breakdown, warranty table and the shop's authorised signature",
  },
  {
    src: "/screenshots/imei-sale.png",
    alt: "NodeDR POS checkout asking for the IMEI of each unit being sold",
    caption: "Optional IMEI capture — asked at the moment of sale, never in stock",
  },
  {
    src: "/screenshots/warranty.png",
    alt: "NodeDR POS warranty lookup showing sale date and warranty status for an IMEI",
    caption: "Warranty lookup — scan an IMEI to see the sale date and what's left",
  },
  {
    src: "/screenshots/product-imei.png",
    alt: "NodeDR POS product settings for optional serial number and IMEI tracking",
    caption: "Product tracking — enable serial number or IMEI capture for selected products",
  },
  {
    src: "/screenshots/features.png",
    alt: "NodeDR POS feature settings for enabling retail workflows",
    caption: "Settings — choose the shop workflows and tools you want to use",
  },
];

export type FAQ = { q: string; a: string };

export const faqs: FAQ[] = [
  {
    q: "Is NodeDR POS really free?",
    a: "Yes. NodeDR POS is AGPL-3.0-licensed open-source software. There is no subscription, no per-transaction fee, and no premium tier. You download the code, run it on your own machine, and it costs nothing to operate beyond the hardware.",
  },
  {
    q: "Does it work without internet?",
    a: "Yes. Billing, inventory, printing and reports run entirely on your local machine with no internet needed. A few features are optional and use the internet only when you use them: checking for updates, finding product details online by name or barcode (only that text is sent), and live exchange rates or store integrations if you set them up. Your sales data never leaves the shop.",
  },
  {
    q: "What hardware do I need?",
    a: "Any machine that can run Docker: a low-end Windows, Linux, or macOS PC works fine. For the barcode scanner, any USB HID keyboard-wedge scanner (the kind that 'types' the barcode then presses Enter) works plug-and-play. For thermal printing, a generic USB ESC/POS printer (58mm or 80mm roll) or any printer connected via CUPS or the Windows print spooler.",
  },
  {
    q: "Does it support Indian GST?",
    a: "Yes. You can set per-product GST rates and HSN/SAC codes. MRP is treated as GST-inclusive as required by law — CGST/SGST are always broken out separately on the receipt. Live GSTIN format validation is built in. You can also import the official HSN/SAC CSV for autocomplete.",
  },
  {
    q: "Can I use it on a tablet at the counter?",
    a: "Yes. The app runs on port 1994 of the machine running Docker. Any device on the same network — a counter tablet, a phone, a second PC — can open it in a browser and use the full POS interface, including checkout. No per-device install is needed.",
  },
  {
    q: "How do I install it?",
    a: "Choose one of three supported options: install the Windows app from the Microsoft Store, deploy the CasaOS app on a CasaOS or ZimaOS box, or start the Docker Compose stack on Windows, macOS, Linux, a NAS, or a VPS. The first launch guides you through creating an admin account and configuring your shop.",
  },
  {
    q: "Do I need Docker to run it?",
    a: "The Microsoft Store app runs without Docker on Windows. For Windows, macOS, and Linux servers or computers, Docker Compose is supported. CasaOS and ZimaOS users can install the CasaOS app manifest.",
  },
  {
    q: "What happens to my data if I stop the containers?",
    a: "All data (SQLite database and the session secret) lives in a named Docker volume (nodedr-pos_data), not inside the containers. Stopping or removing the containers leaves the volume untouched. Your data survives image rebuilds and system restarts.",
  },
  {
    q: "Is it only for India?",
    a: "No. Choose a shop currency such as INR, USD, EUR, GBP, AED, AUD or CAD in Settings. Currency settings control the symbol on receipts; configured exchange rates also support foreign-currency payments. GST and UPI are India-specific options. Currency support does not establish compliance with every country's tax, invoice or fiscal-reporting rules; check the requirements where your shop operates.",
  },
  {
    q: "Can I connect my online store so stock stays accurate on both?",
    a: "Yes. Link a product by its SKU in Settings > Integrations, then your website's backend can call the External Stock API to read live stock and report a sale, keeping the counter and the site on the same real count — a sale on either side updates the other, and an optional webhook pushes stock changes to your site the moment they happen at the counter.",
  },
];

export type ComparisonRow = {
  feature: string;
  nodedrpos: boolean | string;
  hosted: boolean | string;
  spreadsheet: boolean | string;
};

export const comparisonRows: ComparisonRow[] = [
  { feature: "One-time cost",       nodedrpos: "Free",          hosted: "Monthly fee",    spreadsheet: "Free" },
  { feature: "Works offline",       nodedrpos: true,            hosted: false,            spreadsheet: true },
  { feature: "Barcode scanning",    nodedrpos: true,            hosted: true,             spreadsheet: false },
  { feature: "GST billing (India)", nodedrpos: true,            hosted: "Paid add-on",   spreadsheet: "Manual" },
  { feature: "Thermal printing",    nodedrpos: true,            hosted: true,             spreadsheet: false },
  { feature: "Loyalty program",     nodedrpos: true,            hosted: "Paid tier",      spreadsheet: false },
  { feature: "Returns & exchange",  nodedrpos: true,            hosted: "Paid tier",      spreadsheet: false },
  { feature: "Sales dashboard",     nodedrpos: true,            hosted: true,             spreadsheet: "Manual" },
  { feature: "Data stays on-prem",  nodedrpos: true,            hosted: false,            spreadsheet: true },
  { feature: "No vendor lock-in",   nodedrpos: true,            hosted: false,            spreadsheet: true },
  { feature: "Open source (AGPL-3.0)", nodedrpos: true,         hosted: false,            spreadsheet: false },
];
